import Button, { ButtonType } from '@/components/Button';
import DictTable from '@/components/DictTable';
import DollarInput from '@/components/DollarInput';
import Modal, { IModalProps } from '@/components/Modal';
import useCurrentUser from '@/hooks/useCurrentUser';
import MarketService from '@/lib/Market';
import OrderService from '@/lib/Order';
import { CreateOrderBody, OrderSide } from '@/lib/Order/types';
import WatchlistService from '@/lib/Watchlist';
import Analytics from '@/system/Analytics';
import { OrderEvents } from '@/system/Analytics/events/OrderEvents';
import MathUtils from '@/utils/MathUtils';
import StringUtils from '@/utils/StringUtils';
import { Dialog } from '@headlessui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Icon24Cancel } from '@vkontakte/icons';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { getBtnState, getInfo } from './utils';

export interface OrderAssetInfo {
  name: string;
  symbol: string;
  availableToSell?: number; // Total value of this asset (same as how much they have available to sell)
  sharesOwned?: number; // How many shares they own of this asset already
  latestPrice: number;
}

interface IOrderModalProps extends IModalProps {
  orderSide: OrderSide;
  asset: OrderAssetInfo;
  buyingPower?: number; // How much buying power they have available to buy
  isCrypto?: boolean;
}

export default function OrderModal({
  open,
  closeModal,
  orderSide,
  asset,
  buyingPower,
  isCrypto = false,
}: IOrderModalProps) {
  const [amount, setAmount] = useState(0);
  const { data: user } = useCurrentUser();
  const queryClient = useQueryClient();
  const totalValue = (orderSide === OrderSide.BUY ? buyingPower : asset?.availableToSell) || 0;
  const preselectedPurchaseAmounts = [5, 10, 20].filter((n) => n <= totalValue);
  const [resetDollarInput, setResetDollarInput] = useState<number>(0);
  const [marketOpen, setMarketOpen] = useState<boolean>(false);

  const createOrderMutation = useMutation(OrderService.createOrder, {
    onSuccess: async (completedOrder, params) => {
      Analytics.track(
        orderSide === OrderSide.BUY
          ? OrderEvents.SUBMIT_BUY_SUCCESS
          : OrderEvents.SUBMIT_SELL_SUCCESS,
        {
          ...params,
          ...completedOrder,
        }
      );
      queryClient.invalidateQueries(['user']);
      queryClient.invalidateQueries(['positions']);
      queryClient.invalidateQueries(['position', asset.symbol]);
      queryClient.invalidateQueries(['portfolioGraphData']);
      queryClient.invalidateQueries(['tradingAccount']);
      setTimeout(() => {
        queryClient.invalidateQueries(['orders']);
        if (!marketOpen) {
          queryClient.invalidateQueries(['pendingOrders']);
        } else {
          queryClient.invalidateQueries(['position', asset.symbol]);
        }
      }, 2000);
    },
    onMutate: async (params) => {
      const marketOpen = await MarketService.isMarketOpen();
      setMarketOpen(marketOpen);
    },
    onError: (error, params) => {
      Analytics.track(
        orderSide === OrderSide.BUY ? OrderEvents.SUBMIT_BUY_ERROR : OrderEvents.SUBMIT_SELL_ERROR,
        {
          ...params,
          // @ts-ignore
          error: (error?.response?.data || {}),
        }
      );
      console.log('ERROR CREATING ORDER', error);
    },
  });

  const toggleFollowMutation = useMutation(WatchlistService.toggleFollowWatchlistAsset, {
    onSuccess: (data) => {
      queryClient.invalidateQueries(['watchlist']);
    },
    onError: (error) => {
      console.log("COULDN'T UNFOLLOW WATCHLIST");
    },
  });

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (!open) {
      timeout = setTimeout(() => {
        setAmount(0);
        setResetDollarInput(0);
        createOrderMutation.reset();
      }, 250);
      return () => clearTimeout(timeout);
    }
  }, [open]);

  const display = getInfo({
    orderSide,
    asset,
    amount,
    buyingPower: buyingPower || 0,
    isOrderSuccess: createOrderMutation.isSuccess,
    completedOrder: createOrderMutation.data,
    marketOpen,
    sellAll: amount.toFixed(2) === asset?.availableToSell?.toFixed(2),
    error: createOrderMutation?.isError ? String(createOrderMutation.error) : '',
  });

  const [btnText, btnDisabled] = getBtnState({
    amount,
    notEnough: display.notEnough,
    notEnoughError: display.notEnoughError,
    orderSide: orderSide,
  });

  const isFirstStep = !createOrderMutation.isError && !createOrderMutation.isSuccess;

  const onSubmit = async () => {
    const symbol = isCrypto ? `${asset.symbol}USD` : asset.symbol;
    const sellAll = amount.toFixed(2) === asset?.availableToSell?.toFixed(2);

    // try to create order to Alpaca OR make order request
    const body: CreateOrderBody = {
      userId: user?.account?.id as string,
      symbol,
      side: orderSide,
      sellAll,
      isCrypto,
    };

    if (sellAll) {
      body.qty = asset.sharesOwned;

      await toggleFollowMutation.mutateAsync({
        symbol,
        setFollow: false,
      });
    } else if (isCrypto) {
      // Buying/selling crypto
      // TODO: Do this when crypto is being implemented
      // body.qty = expectedQty;
    } else {
      // Buying/selling a regular stock
      body.notional = amount;
    }

    Analytics.track(
      orderSide === OrderSide.BUY ? OrderEvents.SUBMIT_BUY : OrderEvents.SUBMIT_SELL,
      {
        symbol,
        amount: body.notional,
        qty: body.qty,
        sellAll,
        isCrypto,
      }
    );

    // TODO not sure what this is for
    // if (orderSide === OrderSide.BUY && asset.sharesOwned === params.qty) {
    //   await toggleFollowMutation.mutateAsync({
    //     symbol,
    //     setFollow: true,
    //   });
    // }

    createOrderMutation.mutate(body);

    // TODO: have to add PDT to orders
    // if (params.pushToPDT) {
    //   dispatch(addSymbolToPdt(symbol)); // in case this symbol was marked for PDT....now a transaction occourred.....time to push it!
    // }
  };

  const renderBody = () => {
    return (
      <>
        {isFirstStep ? (
          <>
            <DollarInput
              initialAmount={amount}
              resetAmount={resetDollarInput}
              onChangeAmount={(value) => setAmount(value ? parseFloat(value) : 0)}
            />
            {(preselectedPurchaseAmounts?.length > 0 || orderSide === OrderSide.SELL) && (
              <div className="flex space-x-3 mt-6">
                {preselectedPurchaseAmounts.map((amt) => {
                  return (
                    <Button
                      key={amt}
                      type={ButtonType.Tab}
                      className={clsx('rounded-full w-auto text-primary px-2', {
                        'bg-btn-overlay': amt === amount,
                        'bg-btn-overlay-secondary': amt !== amount,
                      })}
                      buttonType="button"
                      onClick={() => {
                        setAmount(amt);
                        setResetDollarInput(amt);
                      }}
                    >
                      {StringUtils.USD(amt)}
                    </Button>
                  );
                })}
                {orderSide === OrderSide.SELL && (
                  <Button
                    type={ButtonType.Tab}
                    className={clsx('rounded-full w-auto text-primary px-2', {
                      'bg-btn-overlay': asset?.availableToSell === amount,
                      'bg-btn-overlay-secondary': asset?.availableToSell !== amount,
                    })}
                    buttonType="button"
                    onClick={() => {
                      setAmount(asset?.availableToSell || 0);
                      setResetDollarInput(asset?.availableToSell || 0);
                    }}
                  >
                    Sell all
                  </Button>
                )}
              </div>
            )}
          </>
        ) : (
          display.completeState
        )}
        {!createOrderMutation?.isError && (
          <>
            <DictTable
              classNames={{ container: 'px-3 mt-12', row: 'py-2', value: 'font-medium' }}
              columns={[
                [
                  {
                    key: 'Approx. stock price',
                    value: StringUtils.USD(asset.latestPrice),
                  },
                  {
                    key: 'Approx. shares',
                    value: MathUtils.round(amount / asset.latestPrice, 0.0001).toLocaleString(),
                  },
                ],
              ]}
            />
            <DictTable
              classNames={{ container: 'px-3 mt-4 mb-6', row: 'py-2', value: 'font-medium' }}
              columns={[
                [
                  {
                    key: 'Estimated cost',
                    value: StringUtils.USD(amount),
                  },
                ],
              ]}
            />
          </>
        )}
      </>
    );
  };

  const renderBtn = () => {
    if (isFirstStep) {
      return (
        <Button
          disabled={btnDisabled}
          loading={createOrderMutation?.isLoading}
          type={ButtonType.Primary}
          className={clsx('w-full', { 'disabled:text-u-negative': display.notEnough })}
          buttonType="submit"
        >
          {btnText}
        </Button>
      );
    }

    return (
      <Button type={ButtonType.Primary} className="w-full" buttonType="submit">
        {createOrderMutation?.isError ? 'Try again' : 'Gotcha'}
      </Button>
    );
  };

  return (
    // @ts-ignore - TypeScript compatibility issue with React 18 types
    <Modal open={open} closeModal={closeModal}>
      {/* @ts-ignore - TypeScript compatibility issue with React 18 and HeadlessUI */}
      <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl min-h-[450px] bg-secondary p-9 text-left align-middle shadow-xl transition-all h-full">
        {/* @ts-ignore - TypeScript compatibility issue with React 18 types */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            return isFirstStep
              ? onSubmit()
              : createOrderMutation?.isError
              ? createOrderMutation.reset()
              : closeModal();
          }}
          className="flex flex-col justify-between min-h-[400px] h-full"
        >
          <div>
            {isFirstStep && (
              <Dialog.Title as="h2" className="font-semibold text-center px-16">
                {display.title}
              </Dialog.Title>
            )}
            {isFirstStep && <h4 className="text-center text-secondary mt-1">{display.subtitle}</h4>}
            <Button
              type={ButtonType.Link}
              className="absolute w-6 h-6 top-9 right-9 text-icon-primary z-50"
              onClick={closeModal}
              buttonType="button"
            >
              <Icon24Cancel className="w-full h-full" />
            </Button>
          </div>
          <div className="flex flex-col justify-center items-center mt-12 h-full">
            {renderBody()}
          </div>
          <div>{renderBtn()}</div>
        </form>
      </Dialog.Panel>
    </Modal>
  );
}
